import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Button,
    Checkbox,
    Divider,
    Form,
    Input,
    Select,
    Space,
    Switch,
    Timeline,
    Typography,
} from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";
import { ClientSettingAuditRow } from "../../../model/client/ClientSettingAuditRow";
import {
    DEFAULT_MEMBERSHIP_CARD_LAYOUT,
    MembershipCardFieldType,
    MembershipCardHeaderType,
    MembershipCardLayout,
} from "../../../model/client/ClientSettings";

const { Text, Paragraph } = Typography;

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

const FIELD_TYPE_OPTIONS: { value: MembershipCardFieldType; label: string }[] = [
    { value: "memberName", label: "Member name" },
    { value: "membershipId", label: "Membership ID" },
    { value: "companyName", label: "Company name" },
];

const HEADER_TYPE_OPTIONS: { value: MembershipCardHeaderType; label: string }[] = [
    { value: "text", label: "Club name + tagline (text)" },
    { value: "logo", label: "Logo image" },
];

// A layout is saveable when it has at least one field and the optional header
// colour, when set, is a #RRGGBB hex. Mirrors the server-side validation so the
// Save button is only enabled for payloads the API will accept.
export function isMembershipCardLayoutValid(layout: MembershipCardLayout): boolean {
    if (!layout.fields || layout.fields.length === 0) return false;
    const bg = layout.header.backgroundColor;
    if (bg !== null && bg.trim() !== "" && !HEX_COLOR.test(bg.trim())) return false;
    return true;
}

// Belt-and-braces: the API always serves a complete layout, but a stale cache or
// an older stored shape could omit a sub-object. Merge the loaded layout onto the
// default so the editor can never dereference a missing header/fields/barcode and
// blank the settings page.
export function normalizeMembershipCardLayout(
    loaded: MembershipCardLayout | null | undefined,
): MembershipCardLayout {
    if (!loaded) return DEFAULT_MEMBERSHIP_CARD_LAYOUT;
    return {
        ...DEFAULT_MEMBERSHIP_CARD_LAYOUT,
        ...loaded,
        header: { ...DEFAULT_MEMBERSHIP_CARD_LAYOUT.header, ...(loaded.header ?? {}) },
        fields:
            Array.isArray(loaded.fields) && loaded.fields.length > 0
                ? loaded.fields
                : DEFAULT_MEMBERSHIP_CARD_LAYOUT.fields,
        barcode: { ...DEFAULT_MEMBERSHIP_CARD_LAYOUT.barcode, ...(loaded.barcode ?? {}) },
    };
}

// Controlled editor for the membership-card layout: photo toggle, header
// (text/logo + optional background colour), an ordered list of fields, and the
// barcode toggle. The shell-mounted panel below owns load/save/history.
export const MembershipCardSection: React.FC<SectionPanelProps<MembershipCardLayout>> = ({
    value,
    onChange,
    readOnly,
}) => {
    const updateField = (index: number, patch: Partial<MembershipCardLayout["fields"][number]>) => {
        const fields = value.fields.map((f, i) => (i === index ? { ...f, ...patch } : f));
        onChange({ ...value, fields });
    };

    const moveField = (index: number, delta: number) => {
        const target = index + delta;
        if (target < 0 || target >= value.fields.length) return;
        const fields = [...value.fields];
        [fields[index], fields[target]] = [fields[target], fields[index]];
        onChange({ ...value, fields });
    };

    const removeField = (index: number) => {
        onChange({ ...value, fields: value.fields.filter((_, i) => i !== index) });
    };

    // Field types are a closed set; offering the same type twice would render a
    // duplicate line. Default a new field to the first type not already present
    // and disable adding once all three are in use.
    const usedTypes = new Set(value.fields.map((f) => f.type));
    const nextUnusedType = FIELD_TYPE_OPTIONS.find((o) => !usedTypes.has(o.value))?.value;

    const addField = () => {
        if (!nextUnusedType) return;
        onChange({
            ...value,
            fields: [
                ...value.fields,
                {
                    type: nextUnusedType,
                    label: null,
                    hideWhenEmpty: nextUnusedType === "companyName",
                },
            ],
        });
    };

    return (
        <Form layout="vertical" data-testid="settings-membership-card-form" disabled={readOnly}>
            <Form.Item label="Show member photo" help="Reserves a photo column on the right of the card.">
                <Switch
                    data-testid="settings-card-show-photo"
                    checked={value.showPhoto}
                    onChange={(checked) => onChange({ ...value, showPhoto: checked })}
                />
            </Form.Item>

            <Form.Item label="Header style">
                <Select
                    data-testid="settings-card-header-type"
                    style={{ width: "100%", maxWidth: 360 }}
                    value={value.header.type}
                    options={HEADER_TYPE_OPTIONS}
                    onChange={(next: MembershipCardHeaderType) =>
                        onChange({ ...value, header: { ...value.header, type: next } })
                    }
                />
            </Form.Item>

            <Form.Item
                label="Header background colour"
                help="Optional #RRGGBB. Leave blank to use the branding membership-card colour."
                validateStatus={
                    value.header.backgroundColor &&
                    value.header.backgroundColor.trim() !== "" &&
                    !HEX_COLOR.test(value.header.backgroundColor.trim())
                        ? "error"
                        : undefined
                }
            >
                <Input
                    data-testid="settings-card-header-bg"
                    style={{ maxWidth: 200 }}
                    placeholder="#1E2878"
                    value={value.header.backgroundColor ?? ""}
                    onChange={(e) => {
                        const raw = e.target.value;
                        onChange({
                            ...value,
                            header: { ...value.header, backgroundColor: raw.trim() === "" ? null : raw },
                        });
                    }}
                />
            </Form.Item>

            {value.header.type === "logo" && (
                <Form.Item
                    label="Logo image key"
                    help="S3 object key of the header logo in the assets bucket, e.g. logos/your-club.png. Leave blank to fall back to the club name text."
                >
                    <Input
                        data-testid="settings-card-logo-key"
                        style={{ width: "100%", maxWidth: 360 }}
                        placeholder="logos/your-club.png"
                        value={value.header.logoKey ?? ""}
                        onChange={(e) => {
                            const raw = e.target.value;
                            onChange({
                                ...value,
                                header: { ...value.header, logoKey: raw.trim() === "" ? null : raw },
                            });
                        }}
                    />
                </Form.Item>
            )}

            <Divider orientation="left">Fields (top to bottom)</Divider>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                {value.fields.map((field, index) => (
                    <Space key={index} align="baseline" wrap data-testid={`settings-card-field-${index}`}>
                        <Select
                            data-testid={`settings-card-field-type-${index}`}
                            style={{ width: 170 }}
                            value={field.type}
                            options={FIELD_TYPE_OPTIONS}
                            onChange={(next: MembershipCardFieldType) => updateField(index, { type: next })}
                        />
                        <Input
                            data-testid={`settings-card-field-label-${index}`}
                            style={{ width: 180 }}
                            placeholder="Label (optional)"
                            value={field.label ?? ""}
                            onChange={(e) => updateField(index, { label: e.target.value === "" ? null : e.target.value })}
                        />
                        <Checkbox
                            data-testid={`settings-card-field-hide-${index}`}
                            checked={field.hideWhenEmpty}
                            onChange={(e) => updateField(index, { hideWhenEmpty: e.target.checked })}
                        >
                            Hide when empty
                        </Checkbox>
                        <Button
                            type="text"
                            icon={<ArrowUpOutlined />}
                            disabled={index === 0}
                            onClick={() => moveField(index, -1)}
                            aria-label="Move field up"
                        />
                        <Button
                            type="text"
                            icon={<ArrowDownOutlined />}
                            disabled={index === value.fields.length - 1}
                            onClick={() => moveField(index, 1)}
                            aria-label="Move field down"
                        />
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={value.fields.length <= 1}
                            onClick={() => removeField(index)}
                            aria-label="Remove field"
                            data-testid={`settings-card-field-remove-${index}`}
                        />
                    </Space>
                ))}
                <Button
                    icon={<PlusOutlined />}
                    onClick={addField}
                    disabled={readOnly || !nextUnusedType}
                    data-testid="settings-card-add-field"
                >
                    Add field
                </Button>
            </Space>

            <Divider orientation="left">Barcode</Divider>
            <Form.Item label="Show barcode" help="CODE128 barcode of the membership ID at the foot of the card.">
                <Switch
                    data-testid="settings-card-show-barcode"
                    checked={value.barcode.show}
                    onChange={(checked) => onChange({ ...value, barcode: { ...value.barcode, show: checked } })}
                />
            </Form.Item>
        </Form>
    );
};

// Adapter that mounts MembershipCardSection inside the Settings shell: loads the
// current layout from /config, tracks dirty state, saves via PUT, and shows the
// recent change history (keyPrefix 'membershipCard.').
export const MembershipCardSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;
    const [initial, setInitial] = useState<MembershipCardLayout | null>(null);
    const [value, setValue] = useState<MembershipCardLayout>(DEFAULT_MEMBERSHIP_CARD_LAYOUT);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [history, setHistory] = useState<ClientSettingAuditRow[]>([]);

    // History is best-effort: a failed load leaves the editor fully usable, so
    // errors are swallowed (mirrors ThemeSection).
    const refreshHistory = useCallback(async () => {
        if (!Number.isFinite(clientId)) {
            setHistory([]);
            return;
        }
        try {
            const rows = await service.getSettingsHistory(clientId, {
                keyPrefix: "membershipCard.",
                limit: 20,
            });
            setHistory(rows);
        } catch {
            setHistory([]);
        }
    }, [service, clientId]);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            setLoadError("Invalid client id in URL.");
            return;
        }
        setLoadError(null);
        let cancelled = false;
        (async () => {
            try {
                const config = await service.getConfig(clientId);
                if (cancelled) return;
                const loaded = normalizeMembershipCardLayout(config?.settings.membershipCard);
                setInitial(loaded);
                setValue(loaded);
                await refreshHistory();
            } catch (err) {
                if (!cancelled) setLoadError(err instanceof Error ? err.message : "Failed to load settings.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [service, clientId, refreshHistory]);

    const dirty = initial !== null && JSON.stringify(initial) !== JSON.stringify(value);
    const valid = isMembershipCardLayoutValid(value);

    const handleSave = async () => {
        if (!Number.isFinite(clientId) || !valid) return;
        setSaving(true);
        setSaveError(null);
        try {
            // Normalise blank header colour / logo key to null before sending.
            const payload: MembershipCardLayout = {
                ...value,
                header: {
                    ...value.header,
                    backgroundColor:
                        value.header.backgroundColor && value.header.backgroundColor.trim() !== ""
                            ? value.header.backgroundColor.trim()
                            : null,
                    logoKey:
                        value.header.logoKey && value.header.logoKey.trim() !== ""
                            ? value.header.logoKey.trim()
                            : null,
                },
            };
            await service.updateMembershipCard(clientId, payload);
            setInitial(payload);
            setValue(payload);
            await refreshHistory();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-membership-card-load-error" />;
    }
    if (initial === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Controls the printed membership card: whether it shows a photo, the header style, which
                member fields appear (and in what order), and the barcode.
            </Paragraph>
            <MembershipCardSection value={value} onChange={setValue} />
            {saveError && (
                <Alert type="error" message={saveError} data-testid="settings-membership-card-save-error" />
            )}
            <Space>
                <Button
                    type="primary"
                    data-testid="settings-membership-card-save-btn"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!dirty || !valid}
                >
                    Save changes
                </Button>
                <Button
                    data-testid="settings-membership-card-discard-btn"
                    onClick={() => initial && setValue(initial)}
                    disabled={!dirty || saving}
                >
                    Discard
                </Button>
            </Space>
            {history.length > 0 && (
                <>
                    <Divider orientation="left">Change history</Divider>
                    <Timeline
                        data-testid="settings-membership-card-history"
                        items={history.map((row) => {
                            // changedAt is revived to a Date by the API client's
                            // DateReviver, but a value that fails to revive would
                            // otherwise throw here and blank the whole page — guard
                            // it (mirrors ThemeSection's history rendering).
                            const changedAtDisplay =
                                row.changedAt instanceof Date && !Number.isNaN(row.changedAt.getTime())
                                    ? row.changedAt.toLocaleString()
                                    : String(row.changedAt);
                            return {
                                children: (
                                    <Text type="secondary">
                                        {row.operation} by {row.changedBy} — {changedAtDisplay}
                                    </Text>
                                ),
                            };
                        })}
                    />
                </>
            )}
        </Space>
    );
};
